/* eslint-disable security/detect-object-injection */
import {
  useApplicationContext,
  useAppState,
  usePage,
} from '@bees-web/core/shared/contexts'
import { IApplicationState, IBlock, QueryParams } from '@bees-web/core/types'
import { useMemo, useRef } from 'react'

import { RequestHeaders } from '../types/interfaces'
// eslint-disable-next-line import/no-cycle
import { getDeferredUrl, jokerUrlBuilder } from '../utils/joker'
import { generateDeferredBlockTraceId } from '../utils/requestTrace'
import { processUrlStateExpression, urlToObjectParams } from '../utils/url'

import useFetch from './useFetch'

export const blockUrlBuilder = (
  url: string,
  params: QueryParams,
  state: IApplicationState = null
): string =>
  jokerUrlBuilder('blocks', {
    ...urlToObjectParams(processUrlStateExpression(url, state)),
    ...params,
  })

/**
 * Keeps a stable string reference: only updates the ref when the
 * computed string value actually differs from the previous one.
 * Prevents SWR key churn when `state` reference changes but
 * the resolved URL is identical.
 */
function useStableUrl(computedUrl: string): string {
  const ref = useRef(computedUrl)
  if (computedUrl !== ref.current) {
    ref.current = computedUrl
  }
  return ref.current
}

export default function useBlockSchema(template: IBlock): {
  isLoading: boolean
  error: any
  block: IBlock
} {
  const deferredUrl = getDeferredUrl(template)
  const url = deferredUrl?.url
  const deferredHeaders = deferredUrl?.headers

  const {
    session: {
      accountInfo: { accountId: selectedPocAccount, storeId },
    },
    tracking: { requestTraceId },
    locale: { country, language },
  } = useApplicationContext()
  const { state } = useAppState()
  const { query } = usePage()

  const coreQs = useMemo<QueryParams>(
    () => ({
      ...query,
      locale: `${language}_${country}`,
      ...(selectedPocAccount ? { selectedPocAccount } : {}),
      ...(storeId ? { storeId } : {}),
    }),
    [query, language, country, selectedPocAccount, storeId]
  )

  const rawBlockUrl = useMemo(
    () => (url ? blockUrlBuilder(url, coreQs, state) : null),
    [url, coreQs, state]
  )

  const blockUrl = useStableUrl(rawBlockUrl)

  const fetchHeaders = useMemo<RequestHeaders>(
    () => ({
      ...deferredHeaders,
      requestTraceId: generateDeferredBlockTraceId(requestTraceId, template.name),
    }),
    [deferredHeaders, requestTraceId, template.name]
  )

  const { data: block, error, isLoading } = useFetch<IBlock>(blockUrl, fetchHeaders)

  if (error) {
    console.error(blockUrl, error)
  }

  return { isLoading, error, block }
}
